import React from "react";

const About = () => {
  return (
    <main
      className="
        min-h-screen
        w-full
        overflow-x-hidden
        bg-[#f6f7fb]
        text-slate-900
        transition-colors
        duration-300
        dark:bg-[#05030a]
        dark:text-white
      "
    >
      {/* =====================================================
          HERO
      ===================================================== */}
      <section
        id="game-title"
        className="
          relative
          flex
          min-h-[190px]
          w-full
          items-center
          justify-center
          overflow-hidden
          bg-slate-100
          dark:bg-[#06040d]
          sm:min-h-[250px]
          md:min-h-[310px]
          lg:min-h-[350px]
        "
      >
        {/* Main purple glow */}
        <div
          className="
            pointer-events-none
            absolute
            left-1/2
            top-1/2
            h-[180px]
            w-[280px]
            -translate-x-1/2
            -translate-y-1/2
            rounded-full
            bg-purple-500/10
            blur-[80px]
            dark:bg-purple-600/[0.14]
            sm:h-[280px]
            sm:w-[430px]
            sm:blur-[110px]
          "
        />

        {/* Cyan glow */}
        <div
          className="
            pointer-events-none
            absolute
            -left-20
            top-5
            h-[140px]
            w-[140px]
            rounded-full
            bg-cyan-400/10
            blur-[70px]
            dark:bg-cyan-500/[0.08]
            sm:h-[220px]
            sm:w-[220px]
            sm:blur-[100px]
          "
        />

        {/* Gold glow */}
        <div
          className="
            pointer-events-none
            absolute
            -right-20
            bottom-0
            h-[160px]
            w-[160px]
            rounded-full
            bg-amber-300/10
            blur-[75px]
            dark:bg-[#d6b36a]/[0.08]
            sm:h-[230px]
            sm:w-[230px]
            sm:blur-[100px]
          "
        />

        {/* Grid */}
        <div
          className="
            pointer-events-none
            absolute
            inset-0
            opacity-[0.035]
            [background-image:linear-gradient(rgba(15,23,42,0.4)_1px,transparent_1px),linear-gradient(90deg,rgba(15,23,42,0.4)_1px,transparent_1px)]
            [background-size:32px_32px]
            dark:opacity-[0.045]
            dark:[background-image:linear-gradient(rgba(255,255,255,0.3)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.3)_1px,transparent_1px)]
          "
        />

        {/* Rings */}
        <div
          className="
            pointer-events-none
            absolute
            left-1/2
            top-1/2
            hidden
            h-[280px]
            w-[280px]
            -translate-x-1/2
            -translate-y-1/2
            rounded-full
            border
            border-purple-500/[0.06]
            dark:border-purple-400/[0.05]
            sm:block
            sm:h-[360px]
            sm:w-[360px]
          "
        />

        <div
          className="
            pointer-events-none
            absolute
            left-1/2
            top-1/2
            hidden
            h-[210px]
            w-[210px]
            -translate-x-1/2
            -translate-y-1/2
            rounded-full
            border
            border-amber-400/[0.07]
            dark:border-[#d6b36a]/[0.05]
            sm:block
            sm:h-[250px]
            sm:w-[250px]
          "
        />

        {/* Light lines */}
        <div
          className="
            pointer-events-none
            absolute
            left-1/2
            top-[22%]
            h-px
            w-[75%]
            -translate-x-1/2
            bg-gradient-to-r
            from-transparent
            via-purple-500/15
            to-transparent
            dark:via-purple-400/[0.12]
          "
        />

        <div
          className="
            pointer-events-none
            absolute
            bottom-[18%]
            left-1/2
            h-px
            w-[55%]
            -translate-x-1/2
            bg-gradient-to-r
            from-transparent
            via-cyan-500/10
            to-transparent
            dark:via-cyan-400/[0.07]
          "
        />

        {/* Corners */}
        <div
          className="
            absolute
            left-3
            top-4
            h-7
            w-7
            border-l
            border-t
            border-amber-500/20
            dark:border-[#d6b36a]/15
            sm:left-8
            sm:top-7
            sm:h-10
            sm:w-10
          "
        />

        <div
          className="
            absolute
            right-3
            top-4
            h-7
            w-7
            border-r
            border-t
            border-amber-500/20
            dark:border-[#d6b36a]/15
            sm:right-8
            sm:top-7
            sm:h-10
            sm:w-10
          "
        />

        {/* Hero content */}
        <div className="relative z-10 mx-auto w-full max-w-[1100px] px-4 text-center sm:px-5">

          {/* Badge */}
          <div
            className="
              mx-auto
              mb-3
              inline-flex
              items-center
              gap-2
              rounded-full
              border
              border-amber-500/20
              bg-white/60
              px-3
              py-1.5
              shadow-sm
              backdrop-blur-xl
              dark:border-[#d6b36a]/20
              dark:bg-[#0c0814]/70
              dark:shadow-[0_0_25px_rgba(214,179,106,0.05)]
              sm:mb-4
              sm:px-4
              sm:py-2
            "
          >
            <span
              className="
                h-1.5
                w-1.5
                rounded-full
                bg-emerald-500
                shadow-[0_0_8px_rgba(16,185,129,0.7)]
                dark:bg-emerald-400
                dark:shadow-[0_0_10px_rgba(52,211,153,0.7)]
                dark:animate-pulse
                dark:h-2
                dark:w-2
              "
            />

            <span
              className="
                text-[8px]
                font-bold
                uppercase
                tracking-[0.2em]
                text-slate-600
                dark:text-[#d9cda9]
                sm:text-[10px]
                sm:tracking-[0.3em]
              "
            >
              Premium Gaming Platform
            </span>
          </div>

          {/* Title */}
          <h1
            className="
              font-serif
              text-[30px]
              font-black
              tracking-wide
              text-slate-800
              drop-shadow-sm
              dark:text-transparent
              dark:bg-clip-text
              dark:bg-gradient-to-b
              dark:from-white
              dark:via-[#f4dfaa]
              dark:to-[#b78b40]
              dark:drop-shadow-[0_4px_20px_rgba(0,0,0,0.8)]
              sm:text-[44px]
              md:text-[58px]
              lg:text-[68px]
            "
          >
            Haqqımızda
          </h1>

          {/* Subtitle */}
          <p
            className="
              mx-auto
              mt-2
              max-w-[560px]
              text-[11px]
              leading-5
              text-slate-500
              dark:text-white/45
              sm:mt-3
              sm:text-[14px]
              sm:leading-6
              md:text-[15px]
            "
          >
            Oyun dünyasında sürətli, təhlükəsiz və rahat
            ödəniş təcrübəsi üçün yaradılmış premium platforma.
          </p>

          {/* Divider */}
          <div
            className="
              mx-auto
              mt-4
              flex
              max-w-[210px]
              items-center
              gap-2
              sm:mt-6
              sm:max-w-[260px]
              sm:gap-3
            "
          >
            <span className="h-px flex-1 bg-gradient-to-r from-transparent to-amber-500/50 dark:to-[#d6b36a]/50" />

            <span
              className="
                relative
                flex
                h-2.5
                w-2.5
                items-center
                justify-center
                sm:h-3
                sm:w-3
              "
            >
              <span className="absolute h-2.5 w-2.5 rotate-45 border border-amber-500/60 dark:border-[#d6b36a]/60 sm:h-3 sm:w-3" />

              <span className="h-1 w-1 rounded-full bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.8)] dark:bg-[#d6b36a] dark:shadow-[0_0_10px_rgba(214,179,106,0.7)]" />
            </span>

            <span className="h-px flex-1 bg-gradient-to-l from-transparent to-amber-500/50 dark:to-[#d6b36a]/50" />
          </div>
        </div>

        {/* Bottom fade */}
        <div
          className="
            absolute
            bottom-0
            left-0
            right-0
            h-16
            bg-gradient-to-t
            from-[#f6f7fb]
            to-transparent
            dark:from-[#05030a]
            sm:h-24
          "
        />
      </section>


      {/* =====================================================
          CONTENT
      ===================================================== */}
      <section
        className="
          relative
          overflow-hidden
          px-3
          py-7
          sm:px-5
          sm:py-10
          md:px-7
          md:py-14
          lg:px-8
          lg:py-20
        "
      >

        {/* Ambient glows */}
        <div
          className="
            pointer-events-none
            absolute
            -left-32
            top-10
            h-64
            w-64
            rounded-full
            bg-purple-500/[0.06]
            blur-[100px]
            dark:bg-purple-700/[0.08]
          "
        />

        <div
          className="
            pointer-events-none
            absolute
            -right-32
            top-[40%]
            h-64
            w-64
            rounded-full
            bg-cyan-500/[0.05]
            blur-[100px]
            dark:bg-cyan-600/[0.05]
          "
        />

        <div className="relative mx-auto w-full max-w-[1100px]">

          {/* =================================================
              MAIN CARD
          ================================================= */}
          <div
            className="
              relative
              overflow-hidden
              rounded-[22px]
              border
              border-slate-200
              bg-white/80
              p-4
              shadow-[0_20px_60px_rgba(15,23,42,0.08)]
              backdrop-blur-xl

              dark:border-white/[0.06]
              dark:bg-[#090611]
              dark:shadow-[0_25px_80px_rgba(0,0,0,0.55)]

              sm:rounded-[28px]
              sm:p-6
              md:p-9
              lg:p-11
            "
          >

            {/* Dark mode top line */}
            <div
              className="
                pointer-events-none
                absolute
                left-1/2
                top-0
                h-px
                w-[65%]
                -translate-x-1/2
                bg-gradient-to-r
                from-transparent
                via-amber-400/40
                to-transparent
                dark:via-[#d6b36a]/45
              "
            />

            {/* =================================================
                HEADING
            ================================================= */}
            <div className="relative mb-6 flex items-center gap-3 sm:mb-8 sm:gap-4">

              <div
                className="
                  flex
                  h-10
                  w-10
                  shrink-0
                  items-center
                  justify-center
                  rounded-xl
                  border
                  border-amber-400/20
                  bg-amber-400/10
                  shadow-sm

                  dark:border-[#d6b36a]/20
                  dark:bg-[#d6b36a]/[0.06]
                  dark:shadow-[0_0_20px_rgba(214,179,106,0.04)]

                  sm:h-12
                  sm:w-12
                  sm:rounded-2xl
                "
              >
                <span className="text-lg text-amber-600 dark:text-[#f0d69c] sm:text-xl">
                  ✦
                </span>
              </div>

              <div>
                <span
                  className="
                    text-[8px]
                    font-bold
                    uppercase
                    tracking-[0.25em]
                    text-amber-600/70
                    dark:text-[#d6b36a]/60
                    sm:text-[10px]
                    sm:tracking-[0.3em]
                  "
                >
                  About Us
                </span>

                <h2
                  className="
                    mt-0.5
                    font-serif
                    text-[22px]
                    font-bold
                    text-slate-800
                    dark:text-white
                    sm:text-[29px]
                  "
                >
                  Biz kimik?
                </h2>
              </div>

              <div
                className="
                  ml-auto
                  hidden
                  h-px
                  flex-1
                  bg-gradient-to-r
                  from-amber-400/20
                  to-transparent
                  dark:from-[#d6b36a]/15
                  sm:block
                "
              />
            </div>


            {/* =================================================
                INTRO CARD
            ================================================= */}
            <div
              className="
                relative
                mb-6
                overflow-hidden
                rounded-2xl
                border
                border-slate-200
                bg-slate-50/80
                p-4

                dark:border-white/[0.05]
                dark:bg-[#0c0814]

                sm:mb-8
                sm:rounded-3xl
                sm:p-6
                md:p-7
              "
            >

              {/* Dark-only subtle glow */}
              <div
                className="
                  pointer-events-none
                  absolute
                  right-[-30px]
                  top-[-30px]
                  h-24
                  w-24
                  rounded-full
                  bg-[#d6b36a]/[0.035]
                  blur-[50px]
                  dark:block
                "
              />

              <p
                className="
                  relative
                  text-[13px]
                  leading-[1.8]
                  text-slate-600
                  dark:text-[#c4bbc9]
                  sm:text-[15px]
                  sm:leading-[1.9]
                  md:text-[16px]
                "
              >
                <span className="font-semibold text-amber-600 dark:text-[#f0d69c]">
                  FFQADİR
                </span>{" "}
                oyun dünyasında istifadəçilərinə sürətli, rahat və
                təhlükəsiz ödəniş imkanları təqdim edən platformadır.
                Məqsədimiz sevdiyiniz oyunlar üçün lazım olan
                xidmətləri mümkün qədər sadə və etibarlı şəkildə
                təqdim etməkdir.
              </p>
            </div>


            {/* =================================================
                FEATURE CARDS
            ================================================= */}
            <div className="grid gap-3 sm:gap-4 lg:grid-cols-3">

              {/* Security */}
              <div
                className="
                  group
                  rounded-2xl
                  border
                  border-slate-200
                  bg-white/70
                  p-4
                  transition-all
                  duration-300
                  hover:-translate-y-1
                  hover:border-emerald-300
                  hover:shadow-[0_15px_40px_rgba(16,185,129,0.08)]

                  dark:border-white/[0.055]
                  dark:bg-[#0a0710]
                  dark:hover:border-emerald-400/20
                  dark:hover:bg-[#0d0915]
                  dark:hover:shadow-[0_15px_45px_rgba(16,185,129,0.05)]

                  sm:rounded-3xl
                  sm:p-6
                "
              >

                <div
                  className="
                    mb-4
                    flex
                    h-10
                    w-10
                    items-center
                    justify-center
                    rounded-xl
                    border
                    border-emerald-400/20
                    bg-emerald-400/10
                    text-lg
                    transition-transform
                    duration-300
                    group-hover:scale-110

                    sm:h-12
                    sm:w-12
                    sm:rounded-2xl
                    sm:text-xl
                  "
                >
                  🛡️
                </div>

                <h3 className="mb-1.5 text-[15px] font-bold text-slate-800 dark:text-white sm:text-[17px]">
                  Təhlükəsiz ödəniş
                </h3>

                <p className="text-[12px] leading-6 text-slate-500 dark:text-white/45 sm:text-[14px] sm:leading-7">
                  Ödəniş proseslərində təhlükəsizlik və istifadəçi
                  məlumatlarının qorunmasına xüsusi diqqət yetiririk.
                </p>
              </div>


              {/* Speed */}
              <div
                className="
                  group
                  rounded-2xl
                  border
                  border-slate-200
                  bg-white/70
                  p-4
                  transition-all
                  duration-300
                  hover:-translate-y-1
                  hover:border-cyan-300
                  hover:shadow-[0_15px_40px_rgba(6,182,212,0.08)]

                  dark:border-white/[0.055]
                  dark:bg-[#0a0710]
                  dark:hover:border-cyan-400/20
                  dark:hover:bg-[#0d0915]
                  dark:hover:shadow-[0_15px_45px_rgba(34,211,238,0.05)]

                  sm:rounded-3xl
                  sm:p-6
                "
              >

                <div
                  className="
                    mb-4
                    flex
                    h-10
                    w-10
                    items-center
                    justify-center
                    rounded-xl
                    border
                    border-cyan-400/20
                    bg-cyan-400/10
                    text-lg
                    transition-transform
                    duration-300
                    group-hover:scale-110

                    sm:h-12
                    sm:w-12
                    sm:rounded-2xl
                    sm:text-xl
                  "
                >
                  ⚡
                </div>

                <h3 className="mb-1.5 text-[15px] font-bold text-slate-800 dark:text-white sm:text-[17px]">
                  Sürətli xidmət
                </h3>

                <p className="text-[12px] leading-6 text-slate-500 dark:text-white/45 sm:text-[14px] sm:leading-7">
                  Oyun kreditləri və digər rəqəmsal məhsullar üçün
                  sürətli və rahat xidmət təcrübəsi yaratmağa çalışırıq.
                </p>
              </div>


              {/* Support */}
              <div
                className="
                  group
                  rounded-2xl
                  border
                  border-slate-200
                  bg-white/70
                  p-4
                  transition-all
                  duration-300
                  hover:-translate-y-1
                  hover:border-fuchsia-300
                  hover:shadow-[0_15px_40px_rgba(217,70,239,0.08)]

                  dark:border-white/[0.055]
                  dark:bg-[#0a0710]
                  dark:hover:border-fuchsia-400/20
                  dark:hover:bg-[#0d0915]
                  dark:hover:shadow-[0_15px_45px_rgba(217,70,239,0.05)]

                  sm:rounded-3xl
                  sm:p-6
                "
              >

                <div
                  className="
                    mb-4
                    flex
                    h-10
                    w-10
                    items-center
                    justify-center
                    rounded-xl
                    border
                    border-fuchsia-400/20
                    bg-fuchsia-400/10
                    text-lg
                    transition-transform
                    duration-300
                    group-hover:scale-110

                    sm:h-12
                    sm:w-12
                    sm:rounded-2xl
                    sm:text-xl
                  "
                >
                  💬
                </div>

                <h3 className="mb-1.5 text-[15px] font-bold text-slate-800 dark:text-white sm:text-[17px]">
                  24/7 dəstək
                </h3>

                <p className="text-[12px] leading-6 text-slate-500 dark:text-white/45 sm:text-[14px] sm:leading-7">
                  Suallarınız və sifarişlərinizlə bağlı yarana biləcək
                  məsələlərdə sizə dəstək olmağa çalışırıq.
                </p>
              </div>

            </div>


            {/* =================================================
                TWO CONTENT CARDS
            ================================================= */}
            <div className="mt-4 grid gap-3 sm:mt-5 sm:gap-4 lg:grid-cols-2">

              {/* Security */}
              <div
                className="
                  rounded-2xl
                  border
                  border-slate-200
                  bg-white/60
                  p-4

                  dark:border-white/[0.05]
                  dark:bg-[#090611]

                  sm:rounded-3xl
                  sm:p-6
                "
              >

                <div className="mb-3 flex items-center gap-3 sm:mb-5">

                  <div className="h-7 w-1 rounded-full bg-gradient-to-b from-amber-400 to-purple-500 sm:h-8" />

                  <h3 className="text-[16px] font-bold text-slate-800 dark:text-white sm:text-[19px]">
                    Təhlükəsizliyimiz
                  </h3>

                </div>

                <p className="text-[13px] leading-[1.8] text-slate-500 dark:text-[#aaa1b0] sm:text-[15px] sm:leading-[1.9]">
                  İstifadəçilərimizin təhlükəsizliyi bizim üçün əsas
                  prioritetlərdən biridir. Ödəniş əməliyyatlarının
                  təhlükəsiz şəkildə həyata keçirilməsi və şəxsi
                  məlumatların məxfiliyinin qorunması üçün müvafiq
                  təhlükəsizlik tədbirlərindən istifadə edirik.
                </p>
              </div>


              {/* Support */}
              <div
                className="
                  rounded-2xl
                  border
                  border-slate-200
                  bg-white/60
                  p-4

                  dark:border-white/[0.05]
                  dark:bg-[#090611]

                  sm:rounded-3xl
                  sm:p-6
                "
              >

                <div className="mb-3 flex items-center gap-3 sm:mb-5">

                  <div className="h-7 w-1 rounded-full bg-gradient-to-b from-cyan-400 to-fuchsia-500 sm:h-8" />

                  <h3 className="text-[16px] font-bold text-slate-800 dark:text-white sm:text-[19px]">
                    Sizin üçün buradayıq
                  </h3>

                </div>

                <p className="text-[13px] leading-[1.8] text-slate-500 dark:text-[#aaa1b0] sm:text-[15px] sm:leading-[1.9]">
                  Məqsədimiz sadəcə məhsul təqdim etmək deyil, istifadəçi
                  üçün rahat və problemsiz bir təcrübə yaratmaqdır.
                  Əlavə məlumat və ya dəstəyə ehtiyacınız olduqda bizimlə
                  əlaqə saxlaya bilərsiniz.
                </p>
              </div>

            </div>


            {/* =================================================
                FINAL MESSAGE
            ================================================= */}
            <div
              className="
                relative
                mt-5
                overflow-hidden
                rounded-2xl
                border
                border-amber-200
                bg-gradient-to-r
                from-amber-50
                via-purple-50
                to-cyan-50
                p-5
                text-center

                dark:border-[#d6b36a]/15
                dark:bg-[#0b0811]
                dark:bg-gradient-to-r
                dark:from-[#0d0912]
                dark:via-[#0b0811]
                dark:to-[#0a0910]

                sm:mt-7
                sm:rounded-3xl
                sm:p-8
              "
            >

              {/* Very subtle dark glow */}
              <div
                className="
                  pointer-events-none
                  absolute
                  left-1/2
                  top-1/2
                  h-32
                  w-48
                  -translate-x-1/2
                  -translate-y-1/2
                  rounded-full
                  bg-amber-400/10
                  blur-[70px]
                  dark:bg-[#d6b36a]/[0.035]
                "
              />

              <div className="relative z-10">

                <div className="mb-2 text-xl text-amber-600 dark:text-[#d6b36a]">
                  ✦
                </div>

                <h3
                  className="
                    font-serif
                    text-[19px]
                    font-bold
                    text-amber-700
                    dark:text-[#e5cd92]
                    sm:text-[25px]
                  "
                >
                  Bizə güvəndiyiniz üçün təşəkkür edirik.
                </h3>

                <p
                  className="
                    mx-auto
                    mt-2
                    max-w-[600px]
                    text-[12px]
                    leading-6
                    text-slate-500
                    dark:text-white/40
                    sm:mt-3
                    sm:text-[14px]
                    sm:leading-7
                  "
                >
                  Sizin məmnuniyyətiniz bizim üçün ən böyük motivasiyadır.
                  Oyun dünyasında sizinlə birlikdə daha da irəli getmək
                  üçün çalışırıq.
                </p>

              </div>
            </div>


            {/* =================================================
                BOTTOM DECORATION
            ================================================= */}
            <div className="mt-6 flex items-center justify-center gap-3 sm:mt-8 sm:gap-4">

              <span className="h-px flex-1 bg-gradient-to-r from-transparent to-amber-400/30 dark:to-[#d6b36a]/20" />

              <span className="flex items-center gap-1.5">

                <span className="h-1.5 w-1.5 rounded-full bg-purple-400/50" />

                <span className="h-2 w-2 rotate-45 border border-amber-500/60 dark:border-[#d6b36a]/50" />

                <span className="h-1.5 w-1.5 rounded-full bg-cyan-400/50" />

              </span>

              <span className="h-px flex-1 bg-gradient-to-l from-transparent to-amber-400/30 dark:to-[#d6b36a]/20" />

            </div>

          </div>
        </div>
      </section>
    </main>
  );
};

export default About;